import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Bell } from 'lucide-react';

import { usePageHeaderContext } from '../../context/PageHeaderContext';
import { Pagination } from '../../ui-components/listing/Pagination';
import {
  useGetAdminNotificationCountQuery,
  useGetAdminNotificationsQuery,
  useMarkNotificationAsReadMutation,
} from './notificationsApi';
import type { NotificationRecord } from './model';
import { formatTimestampToDate } from '../../utils/common';
import {
  formatRelativeNotificationTime,
  getNotificationBadgeClassName,
  getNotificationBadgeStyle,
  getNotificationBadgeLabel,
  getNotificationRoute,
} from './notificationHelpers';

const PAGE_SIZES = [10, 20, 50, 100];

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { setSubtitle } = usePageHeaderContext();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const { data: notificationCountData } =
    useGetAdminNotificationCountQuery(undefined);
  const {
    data: notificationsResponse,
    isFetching,
  } = useGetAdminNotificationsQuery({
    page,
    limit,
    sortBy: 'addedDate',
    sortDirection: 'desc',
  });
  const [markAsRead] = useMarkNotificationAsReadMutation();

  const notifications = useMemo(() => {
    const items = [...(notificationsResponse?.data ?? [])];
    items.sort((left, right) => {
      const leftTime = new Date(left.addedDate).getTime();
      const rightTime = new Date(right.addedDate).getTime();

      if (leftTime !== rightTime) {
        return rightTime - leftTime;
      }

      return (
        Number(right.userNotificationId || 0) - Number(left.userNotificationId || 0)
      );
    });

    return items;
  }, [notificationsResponse?.data]);
  const totalNotifications = notificationsResponse?.meta?.total ?? 0;
  const totalPages = notificationsResponse?.meta?.totalPages ?? 0;
  const unreadCount = notificationCountData?.data?.count ?? 0;

  useEffect(() => {
    if (totalPages > 0 && page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const subtitle = useMemo(() => {
    const totalText = `${totalNotifications} total notification${
      totalNotifications === 1 ? '' : 's'
    }`;
    return unreadCount > 0 ? `${totalText} • ${unreadCount} unread` : totalText;
  }, [totalNotifications, unreadCount]);

  useEffect(() => {
    setSubtitle(subtitle);
    return () => {
      setSubtitle(undefined);
    };
  }, [setSubtitle, subtitle]);

  const handleNotificationClick = async (notification: NotificationRecord) => {
    if (notification.isRead === 0) {
      try {
        await markAsRead(notification.userNotificationId).unwrap();
      } catch {
        // Allow navigation even if mark-as-read fails.
      }
    }

    const targetRoute = getNotificationRoute(notification);
    if (targetRoute) {
      navigate(targetRoute);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <div className="min-h-0 flex-1 overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-950">
        <div className="h-full max-h-[calc(100vh-21rem)] overflow-y-auto">
          {isFetching ? (
            <div className="p-6 text-sm text-neutral-500 dark:text-neutral-400">
              Loading notifications...
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-neutral-100 text-neutral-500 dark:bg-neutral-900 dark:text-neutral-400">
                <Bell className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-800 dark:text-neutral-100">
                  No notifications available.
                </p>
                <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                  New admin notifications will appear here.
                </p>
              </div>
            </div>
          ) : (
            notifications.map((notification, index) => {
              const isUnread = notification.isRead === 0;
              const badgeLabel = getNotificationBadgeLabel(notification);

              return (
                <button
                  key={notification.userNotificationId}
                  type="button"
                  onClick={() => handleNotificationClick(notification)}
                  className={`flex w-full items-start justify-between gap-4 px-4 py-5 text-left transition-colors ${
                    isUnread
                      ? 'hover:bg-sky-100/80 dark:hover:bg-sky-950/35'
                      : 'hover:bg-neutral-50 dark:hover:bg-neutral-900'
                  } ${
                    index < notifications.length - 1
                      ? 'border-b border-neutral-200 dark:border-neutral-800'
                      : ''
                  }`}
                  style={
                    isUnread
                      ? {
                          backgroundColor: '#eff6ff',
                        }
                      : undefined
                  }
                >
                  <div className="flex min-w-0 flex-1 gap-3">
                    <div className="flex w-4 justify-center pt-1">
                      <span
                        className={`mt-1 block h-2.5 w-2.5 rounded-full ${
                          isUnread
                            ? 'dark:bg-sky-300'
                            : 'opacity-0'
                        }`}
                        style={
                          isUnread
                            ? {
                                backgroundColor: '#4f46e5',
                              }
                            : undefined
                        }
                      />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="text-[1.1rem] font-semibold leading-tight text-neutral-900 dark:text-white">
                        {notification.title || notification.notificationCategory}
                      </div>
                      <div className="mt-1 text-[0.95rem] text-neutral-700 dark:text-neutral-300">
                        {notification.message}
                      </div>
                      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400">
                        <span>
                          {formatRelativeNotificationTime(notification.addedDate)}
                        </span>
                        <span>•</span>
                        <span>{formatTimestampToDate(notification.addedDate)}</span>
                      </div>
                    </div>
                  </div>

                  <span
                    className={`mt-1 inline-flex shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${getNotificationBadgeClassName(
                      notification
                    )}`}
                    style={getNotificationBadgeStyle(notification)}
                  >
                    {badgeLabel}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </div>

      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalItems={totalNotifications}
        itemsPerPage={limit}
        onPageChange={(nextPage) => setPage(nextPage)}
        onItemsPerPageChange={(nextLimit) => {
          setLimit(nextLimit);
          setPage(1);
        }}
        pageSizes={PAGE_SIZES}
      />
    </div>
  );
};

export default NotificationsPage;
